import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useProgress, MAX_RATING, BASE_RATING, SECTIONS } from '../progress';
import { scenarios } from '../data';
import { Piece } from './Board';

export const CHESS_NAV = [
  { to: '/opening', label: 'Opening', piece: 'K', hint: 'About me' },
  { to: '/moves', label: 'Moves', piece: 'R', hint: 'Career' },
  { to: '/puzzles', label: 'Puzzles', piece: 'Q', hint: 'Case studies' },
  { to: '/endgame', label: 'Endgame', piece: 'N', hint: 'Contact' },
];

export function useTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} · Rohan Rout` : 'Rohan Rout · AI Engineering & Infrastructure';
  }, [title]);
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function ModeToggle({ mode }) {
  return (
    <div className="mode-toggle" role="group" aria-label="View mode">
      <Link to="/" className={mode === 'chess' ? 'is-active' : ''} aria-current={mode === 'chess' ? 'page' : undefined}>
        <span className="mode-glyph" aria-hidden="true">♞</span>
        <span>Chess</span>
      </Link>
      <Link to="/classic" className={mode === 'classic' ? 'is-active' : ''} aria-current={mode === 'classic' ? 'page' : undefined}>
        <span className="mode-glyph" aria-hidden="true">≡</span>
        <span>Classic</span>
      </Link>
    </div>
  );
}

function RatingChip() {
  const { rating, title, nextTitle, sectionsVisited, puzzlesDone, reset } = useProgress();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const pct = Math.round(((rating - BASE_RATING) / (MAX_RATING - BASE_RATING)) * 100);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="rating" ref={ref}>
      <button
        type="button"
        className="rating-chip"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="rating-title">{title.short}</span>
        <span className="rating-num">{rating}</span>
        <span className="rating-bar" aria-hidden="true">
          <span style={{ width: `${pct}%` }} />
        </span>
      </button>
      {open && (
        <div className="rating-pop" role="dialog" aria-label="Your rating">
          <p className="eyebrow">Your rating</p>
          <p className="rating-pop-num">
            {rating} <span>{title.name}</span>
          </p>
          <p className="rating-pop-copy">
            {nextTitle
              ? `Explore sections and solve puzzles to climb. ${nextTitle.min - rating} points to ${nextTitle.name}.`
              : 'You have seen every move on this board. Well played.'}
          </p>
          <ul className="rating-list">
            <li>
              <span>Sections explored</span>
              <span className="mono">
                {sectionsVisited}/{SECTIONS.length}
              </span>
            </li>
            <li>
              <span>Puzzles completed</span>
              <span className="mono">
                {puzzlesDone}/{scenarios.length}
              </span>
            </li>
          </ul>
          <button type="button" className="link-button" onClick={reset}>
            Reset progress
          </button>
        </div>
      )}
    </div>
  );
}

function Toast() {
  const { toast } = useProgress();
  return (
    <div className="toast-region" aria-live="polite">
      {toast && (
        <div className="toast" key={toast.id}>
          <span className="toast-points">+{toast.points}</span>
          <span>{toast.message}</span>
          {toast.title && <span className="toast-title">New title: {toast.title}</span>}
        </div>
      )}
    </div>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <span>© {new Date().getFullYear()} Rohan Rout</span>
      <span className="footer-links">
        <a href="/resume.pdf" target="_blank" rel="noopener noreferrer">Résumé</a>
        <a href="https://www.linkedin.com/in/routrohan/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
      </span>
    </footer>
  );
}

export function ChessLayout() {
  return (
    <div className="shell">
      <a href="#main" className="skip-link">Skip to content</a>
      <header className="site-header">
        <div className="header-inner">
          <Link to="/" className="brand" aria-label="Rohan Rout, home">
            <span className="brand-mark" aria-hidden="true">♞</span>
            <span className="brand-name">Rohan Rout</span>
          </Link>
          <nav className="main-nav" aria-label="Sections">
            {CHESS_NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className="nav-link">
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="header-actions">
            <RatingChip />
            <ModeToggle mode="chess" />
          </div>
        </div>
      </header>
      <main id="main" className="main">
        <Outlet />
      </main>
      <Footer />
      <nav className="tab-bar" aria-label="Sections">
        {CHESS_NAV.map((item) => (
          <NavLink key={item.to} to={item.to} className="tab-link">
            <span className="tab-piece">
              <Piece piece={item.piece} />
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <Toast />
    </div>
  );
}

export function PageHeader({ index, eyebrow, title, children }) {
  return (
    <header className="page-header">
      <p className="eyebrow">
        {index && <span className="mono">{index}</span>} {eyebrow}
      </p>
      <h1 className="page-title">{title}</h1>
      {children && <p className="page-lede">{children}</p>}
    </header>
  );
}

// "Previous / next move" links at the bottom of each chess page.
export function NextMove({ current }) {
  const i = CHESS_NAV.findIndex((n) => n.to === current);
  const prev = CHESS_NAV[i - 1];
  const next = CHESS_NAV[i + 1];
  return (
    <nav className="next-move" aria-label="Continue">
      {prev ? (
        <Link to={prev.to} className="next-move-link">
          <span className="eyebrow">← Previous</span>
          <span className="next-move-title">{prev.label}</span>
        </Link>
      ) : (
        <span />
      )}
      {next ? (
        <Link to={next.to} className="next-move-link is-next">
          <span className="eyebrow">Next move →</span>
          <span className="next-move-title">{next.label}</span>
        </Link>
      ) : (
        <Link to="/classic" className="next-move-link is-next">
          <span className="eyebrow">Prefer a document? →</span>
          <span className="next-move-title">Classic résumé</span>
        </Link>
      )}
    </nav>
  );
}
